import {isInstanceOf, isPlainObject} from 'rlink-type-utils';

/**
 * Returns a new object of the same type as the given object
 * filled ( or simply of the same type if a type constructor is
 * given) with the values yielded by the provided generator
 * function.
 *
 * The generator function is called with an entries iterator object
 * for the object being copied, and a function that can be called with
 * the current entry ([key, value] pair) and returns whatever value
 * can be yielded back to the constructor (e.g.. the pair again for
 * Maps, or just the value for Sets, etc.) (see `yieldableFor`)
 *
 * @example
 *   // Shallow copy any iterable
 *   let copy = copyGenerator(obj,
 *     function* (yieldable, entries) {
 *       for (let [key, value] of entries) {
 *         yield yieldable(key, value);
 *       }
 *     }
 *   );
 *
 * @function generatorFn
 * @param {Function} yieldable see `yieldableFor`
 * @param {Iterator} entries   see `entries`
 * @returns {*} whatever value is appropriate for yielding
 *
 * @param  {Collection|Function} obj
 * @param  {function*} generatorFn
 * @return {Collection}
 */
export default function copyGenerator (obj, generatorFn) {
  let iterable = generatorFn(
      this.yieldableFor(obj), this.entries(obj));

  if (isInstanceOf(obj, Array)) {
    return Array.from(iterable);
  } else if (isInstanceOf(obj, Map)) {
    return new Map(iterable);
  } else if (isInstanceOf(obj, Set)) {
    return new Set(iterable);
  } else if (isInstanceOf(obj, WeakMap)) {
    return new WeakMap(iterable);
  } else if (isInstanceOf(obj, WeakSet)) {
    return new WeakSet(iterable);
  } else if (isPlainObject(obj) || obj === Object) {
    let newObj = {};
    for (let [key, value] of iterable) {
      newObj[key] = value;
    }
    return newObj;
  } else {
    return this.NotApplicable;
  }
}



// WEBPACK FOOTER //
// ./lib/rlink-collections/core/copyGenerator.js